import React, { useState } from "react";
import { Node, mergeAttributes } from "@tiptap/core";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import PostPhoto from "./PostPhoto";
import EditPhotoModal from "./EditPhotoModal";
import { photoToken } from "../../utils/postContent";

function clearOtherFocusAttrs(editor, keepMediaUuid) {
  if (!editor) return;
  const { tr } = editor.state;
  let changed = false;
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name !== "photo") return;
    if (node.attrs.mediaUuid === keepMediaUuid) return;
    if (!node.attrs.isFocus) return;
    tr.setNodeMarkup(pos, undefined, { ...node.attrs, isFocus: false });
    changed = true;
  });
  if (changed) editor.view.dispatch(tr);
}

function PhotoNodeView({ node, updateAttributes, deleteNode, editor }) {
  const { mediaUuid, src, description, isFocus } = node.attrs;
  const [editOpen, setEditOpen] = useState(false);

  return (
    <NodeViewWrapper as="div" data-photo={mediaUuid} className="post-photo-node">
      <PostPhoto
        src={src || `/media/${mediaUuid}`}
        description={description || ""}
        maxSize={500}
        lightbox={false}
        onActivate={() => setEditOpen(true)}
      />
      {isFocus ? (
        <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>Fokusbillede</div>
      ) : null}
      <EditPhotoModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        mode="edit"
        photo={{
          mediaUuid,
          src: src || `/media/${mediaUuid}`,
          description: description || "",
          isFocus: Boolean(isFocus),
        }}
        onSaved={(photo) => {
          updateAttributes({
            description: photo.description || "",
            isFocus: Boolean(photo.isFocus),
          });
          if (photo.isFocus) {
            clearOtherFocusAttrs(editor, photo.mediaUuid);
          }
        }}
        onDeleted={() => {
          deleteNode();
        }}
      />
    </NodeViewWrapper>
  );
}

export const Photo = Node.create({
  name: "photo",
  group: "block",
  atom: true,
  draggable: false,
  selectable: true,

  addAttributes() {
    return {
      mediaUuid: { default: null },
      src: { default: null },
      description: { default: "" },
      isFocus: { default: false },
    };
  },

  parseHTML() {
    return [
      {
        tag: "div[data-type='photo']",
        getAttrs: (el) => ({
          mediaUuid: el.getAttribute("data-media-uuid"),
          src: el.getAttribute("data-src") || `/media/${el.getAttribute("data-media-uuid")}`,
          description: el.getAttribute("data-description") || "",
          isFocus: el.getAttribute("data-is-focus") === "true",
        }),
      },
      {
        tag: "p[data-photo]",
        getAttrs: (el) => ({
          mediaUuid: el.getAttribute("data-photo"),
          src: `/media/${el.getAttribute("data-photo")}`,
          description: "",
          isFocus: false,
        }),
      },
    ];
  },

  renderHTML({ node }) {
    const id = node.attrs.mediaUuid;
    return [
      "div",
      mergeAttributes({
        "data-type": "photo",
        "data-media-uuid": id,
        "data-src": node.attrs.src || `/media/${id}`,
        "data-description": node.attrs.description || "",
        "data-is-focus": node.attrs.isFocus ? "true" : "false",
      }),
      photoToken(id),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(PhotoNodeView);
  },

  addCommands() {
    return {
      insertPhoto:
        (attrs) =>
        ({ commands, editor }) => {
          const ok = commands.insertContent({
            type: this.name,
            attrs: {
              mediaUuid: attrs.mediaUuid,
              src: attrs.src || `/media/${attrs.mediaUuid}`,
              description: attrs.description || "",
              isFocus: Boolean(attrs.isFocus),
            },
          });
          if (ok && attrs.isFocus) {
            clearOtherFocusAttrs(editor, attrs.mediaUuid);
          }
          return ok;
        },
    };
  },
});

export default Photo;
